import _ from 'lodash';
import Quill from 'quill/core';
import React from 'react';
import isFileOverlarge from '../../utils/network/isFileOverlarge';
import uploadPostAttachment from '../../utils/network/uploadPostAttachment';
import AttachmentContainer from '../attachment/AttachmentContainer';
import attachmentSizeExceededToast from '../attachmentSizeExceededToast';
import type { ScrollBlot } from '../types';
import showEditorToast from '../utils/showEditorToast';
import Comment from './Comment';
import Diff from './Diff';
import ReactBlockEmbed from './ReactBlockEmbed';
import Reaction from './Reaction';
import VersionChange from './VersionChange';
import Queue from '../utils/Queue';

const Parchment = Quill.import('parchment');

const UPLOADING_PREFIX = 'UPLOADING:';
const UPLOAD_CLIENT_ID = `${UPLOADING_PREFIX}${Math.random()
  .toString(36)
  .substring(2, 10)}`;

export type AttachmentValue = { source: string; name: string };

export const isAttachmentUploaded = ({ source }: AttachmentValue) =>
  !source.startsWith(UPLOADING_PREFIX);

export const shouldShowUploadingAttachment = ({ source }: AttachmentValue) =>
  source.startsWith(UPLOAD_CLIENT_ID);

const fileIdMap = new WeakMap();
const getIdForFile = (file: File) => {
  if (!fileIdMap.has(file)) {
    const id = _.uniqueId(UPLOAD_CLIENT_ID);
    fileIdMap.set(file, id);
  }
  return fileIdMap.get(file);
};

// Remote attachment updates will recreate blots so we need to keep track all attachment blots
// so that once an uploading is finished, we can find the related blot
// even if it and the underlying DOM node has been recreated.
const activeAttachmentBlots = new Set<Attachment>();

const normalizeValue = (value: File[]) =>
  value.map((item) =>
    item instanceof File
      ? { source: getIdForFile(item), size: item.size, name: item.name }
      : item,
  );

class Attachment extends ReactBlockEmbed {
  static attachmentUploadQueue: Queue<string | null> = new Queue();

  static upload(
    file: File,
    callback: (result: string | null | undefined) => void,
  ) {
    this.attachmentUploadQueue
      .enqueue(() =>
        uploadPostAttachment(file).then((result) => result ?? null),
      )
      .then(callback)
      .catch((err) => {
        callback(null);

        const isPayloadTooLarge =
          err.networkError && err.networkError.statusCode === 413;

        if (isPayloadTooLarge) {
          showEditorToast(attachmentSizeExceededToast);
        } else {
          showEditorToast({
            level: 'error',
            message: 'Attachment could not be uploaded.',
          });
        }
      });
  }

  // eslint-disable-next-line class-methods-use-this
  filterValidFiles(files: File[]) {
    const [overlargeFiles, validFiles] = _.partition(files, isFileOverlarge);
    if (overlargeFiles.length) {
      overlargeFiles.forEach(() => {
        showEditorToast(attachmentSizeExceededToast);
      });
    }
    return validFiles;
  }

  static uploadFiles(files: File[]) {
    files.forEach((file) => {
      this.upload(file, (source) => {
        activeAttachmentBlots.forEach((blot) => {
          blot.cleanUpload(file, source);
        });
      });
    });
  }

  static create(value: File[]) {
    const node = super.create(normalizeValue(value));
    this.uploadFiles(value.filter((file) => file instanceof File));
    return node;
  }

  constructor(scroll: ScrollBlot, domNode: Node, value: File[]) {
    super(scroll, domNode, value);

    this.appendAttachment = this.appendAttachment.bind(this);
    this.setState({ value: normalizeValue(value), placeholder: false });
  }

  attach() {
    super.attach();
    activeAttachmentBlots.add(this);
  }

  detach() {
    super.detach();
    activeAttachmentBlots.delete(this);
  }

  format(name: string, value: File[]) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return this.attributes.values();
  }

  appendAttachment(files: File[]) {
    if (!files.length) return;
    const value = Attachment.value(this.domNode);
    if (Array.isArray(value)) {
      const validFiles = this.filterValidFiles(files);
      if (validFiles.length) {
        Attachment.uploadFiles(validFiles);

        const newValue = value.concat(normalizeValue(validFiles));
        this.setValue(newValue);
        this.setState({ value: newValue });
      }
    }
  }

  togglePlaceholder(enabled: boolean) {
    this.setState({ placeholder: enabled });
  }

  cleanUpload(file: File, source: string | null | undefined) {
    const value = Attachment.value(this.domNode);
    if (Array.isArray(value)) {
      const targetIndex = value.findIndex(
        (item) => item.source === getIdForFile(file),
      );
      if (targetIndex !== -1) {
        if (source) {
          value[targetIndex].source = source;
        } else {
          value.splice(targetIndex, 1);
        }
      }
      this.setValue(value);
      this.setState({ value });
    }
  }

  handleDeleteAttachment(source: string) {
    const value = Attachment.value(this.domNode);
    if (Array.isArray(value)) {
      const index = value.findIndex((item) => item.source === source);
      if (index !== -1) {
        value.splice(index, 1);

        const hasVisibleAttachments = value.some(
          (attachment) =>
            isAttachmentUploaded(attachment) ||
            shouldShowUploadingAttachment(attachment),
        );
        if (hasVisibleAttachments) {
          this.setValue(value);
          this.setState({ value });
        } else {
          this.remove();
        }
      }
    }
  }

  isUploading() {
    // @ts-expect-error
    return !this.store.getState().value.every(isAttachmentUploaded);
  }

  // @ts-expect-error
  render(state) {
    return (
      <AttachmentContainer
        attachments={state.value}
        placeholder={state.placeholder}
        onDeleteAttachment={(source) => this.handleDeleteAttachment(source)}
        isAttachmentUploaded={isAttachmentUploaded}
        shouldShowUploadingAttachment={shouldShowUploadingAttachment}
      />
    );
  }
}

Attachment.blotName = 'attachment';
Attachment.className = 'ql-attachment';
Attachment.tagName = 'div';

export default Attachment;
